import { useMutation, useQueryClient } from '@tanstack/react-query';
import { client as sdkClient } from '@blazeteams/sdk/client';
import { useAuthStore } from '@/store/auth-store';
import { requireCurrentSession, sessionRevision, sessionScope, sessionTenantId } from '@/features/auth/session-scope';
import { normalizeLocale, normalizePreference, type LanguagePreference, type Locale } from '@/lib/i18n/locales';
import { LocalizedError } from '@/lib/i18n/errors';
import { applyAccountPreference } from './session';

function unwrap<T>(data: unknown): T {
  return (data && typeof data === 'object' && 'data' in data ? (data as { data: T }).data : data) as T;
}

function captureLocaleAction<T>(value: T, resource: 'preference' | 'tenant') {
  const state = useAuthStore.getState();
  return { value, account: state.user?.id, tenant: sessionTenantId(state), revision: sessionRevision(), key: ['locale', resource, sessionScope(state)] as const };
}

export interface TenantLanguage {
  id: string;
  language: Locale;
  can_edit: boolean;
}

function checkedTenantLanguage(data: unknown, owner: string, key: string): TenantLanguage {
  const tenant = unwrap<Partial<TenantLanguage> | null>(data);
  if (!owner || !tenant || tenant.id !== owner || normalizeLocale(tenant.language) !== tenant.language ||
      typeof tenant.language !== 'string' || typeof tenant.can_edit !== 'boolean' ||
      (data && typeof data === 'object' && 'success' in data && data.success !== true)) {
    throw new LocalizedError('common', key);
  }
  return { id: owner, language: tenant.language as Locale, can_edit: tenant.can_edit };
}

export const localeQueries = {
  preference: () => {
    const { account, revision, key } = captureLocaleAction(undefined, 'preference');
    return {
    queryKey: key,
    queryFn: async () => {
      requireCurrentSession(revision);
      const { data, error } = await sdkClient.get({ url: '/auth/me' });
      requireCurrentSession(revision);
      const user = unwrap<{ id: string; language?: string | null }>(data);
      if (!account || error || !user || user.id !== account) throw new LocalizedError('common', 'language.loadFailed');
      const preference = normalizePreference(user.language);
      await applyAccountPreference(account, preference);
      requireCurrentSession(revision);
      useAuthStore.getState().updateUser({ language: preference });
      return preference;
    },
    staleTime: 5 * 60_000,
    };
  },
  tenant: () => {
    const { account, tenant, revision, key } = captureLocaleAction(undefined, 'tenant');
    return {
    queryKey: key,
    queryFn: async () => {
      requireCurrentSession(revision);
      if (!account || !tenant) throw new LocalizedError('common', 'language.loadFailed');
      const { data, error } = await sdkClient.get({ url: '/tenant/language' });
      requireCurrentSession(revision);
      if (error) throw new LocalizedError('common', 'language.loadFailed');
      return checkedTenantLanguage(data, tenant, 'language.loadFailed');
    },
    refetchOnWindowFocus: true,
    };
  },
};

export function useSaveLanguage() {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ value: preference, account, revision }: ReturnType<typeof captureLocaleAction<LanguagePreference>>) => {
      requireCurrentSession(revision);
      if (!account) {
        if (!await applyAccountPreference('anonymous', preference)) throw new LocalizedError('common', 'language.failed');
        requireCurrentSession(revision);
        return preference;
      }
      const { data, error } = await sdkClient.put({ url: `/users/${account}`, headers: { 'Content-Type': 'application/json' }, body: { language: preference } });
      requireCurrentSession(revision);
      if (error) throw new LocalizedError('common', 'language.saveFailed');
      const updated = unwrap<{ id: string; language?: string | null }>(data);
      // The readback prevents silently accepting a server that ignores the field.
      const { data: confirmation, error: confirmationError } = await sdkClient.get({ url: '/auth/me' });
      const confirmed = unwrap<{ id: string; language?: string | null }>(confirmation);
      requireCurrentSession(revision);
      if (updated?.id !== account || confirmationError || confirmed?.id !== account || normalizePreference(confirmed.language) !== preference) {
        throw new LocalizedError('common', 'language.saveFailed');
      }
      useAuthStore.getState().updateUser({ language: preference });
      if (!await applyAccountPreference(account, preference)) throw new LocalizedError('common', 'language.failed');
      requireCurrentSession(revision);
      return preference;
    },
    onSuccess: (preference, variables) => {
      if (variables.revision !== sessionRevision()) return;
      qc.setQueryData(variables.key, preference);
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
  return {
    ...mutation,
    mutate: (value: LanguagePreference, options?: Parameters<typeof mutation.mutate>[1]) => mutation.mutate(captureLocaleAction(value, 'preference'), options),
    mutateAsync: (value: LanguagePreference, options?: Parameters<typeof mutation.mutateAsync>[1]) => mutation.mutateAsync(captureLocaleAction(value, 'preference'), options),
  };
}

export function useSaveTenantLanguage() {
  const qc = useQueryClient();
  const mutation = useMutation({
    scope: { id: JSON.stringify(captureLocaleAction(undefined, 'tenant').key) },
    mutationFn: async ({ value: language, account, tenant: owner, revision }: ReturnType<typeof captureLocaleAction<Locale>>) => {
      requireCurrentSession(revision);
      if (!account || !owner) throw new LocalizedError('common', 'language.saveFailed');
      const { data: updated, error } = await sdkClient.put({ url: '/tenant/language', headers: { 'Content-Type': 'application/json' }, body: { language } });
      requireCurrentSession(revision);
      if (error) throw new LocalizedError('common', 'language.saveFailed');
      const saved = checkedTenantLanguage(updated, owner, 'language.saveFailed');
      if (saved.language !== language || !saved.can_edit) throw new LocalizedError('common', 'language.saveFailed');
      const { data, error: readError } = await sdkClient.get({ url: '/tenant/language' });
      requireCurrentSession(revision);
      const tenant = checkedTenantLanguage(data, owner, 'language.saveFailed');
      if (readError || tenant?.language !== language) throw new LocalizedError('common', 'language.saveFailed');
      return tenant;
    },
    onSuccess: (tenant, variables) => {
      if (variables.revision === sessionRevision()) {
        qc.setQueryData(variables.key, tenant);
        void qc.invalidateQueries({ queryKey: ['ai', 'config', variables.key[2]], exact: true });
        void qc.invalidateQueries({ queryKey: ['channels', 'default-reply', variables.key[2]] });
      }
    },
  });
  return {
    ...mutation,
    mutate: (value: Locale, options?: Parameters<typeof mutation.mutate>[1]) => mutation.mutate(captureLocaleAction(value, 'tenant'), options),
    mutateAsync: (value: Locale, options?: Parameters<typeof mutation.mutateAsync>[1]) => mutation.mutateAsync(captureLocaleAction(value, 'tenant'), options),
  };
}
